import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { DrawerForm } from "../Common/styles/drawer";
import { MAX_OFFSET_IN_DAYS } from "../../utils/constants";

const repeatedDataTypes = [
  { value: "adverse_event", label: "Adverse Event" },
  { value: "event", label: "Event" },
  { value: "medication", label: "Medication" },
  { value: "repeated_measure", label: "Repeated Measure" },
  { value: "unscheduled_visit", label: "Unscheduled Visit" },
  { value: "other", label: "Other" },
];

let schema = yup.object().shape({
  name: yup.string().trim().required("Study Phase Name is Required"),
  type: yup.string().required("Study Phase Type is Required"),
  description: yup.string().optional(),
  duration: yup
    .number()
    .min(0, "Offset cannot be negative")
    .max(
      MAX_OFFSET_IN_DAYS,
      `Offset cannot be greater than ${MAX_OFFSET_IN_DAYS} days`
    )
    .optional(),
  // namingStrategy: yup
  //   .string()
  //   .required("Study Phase Naming Strategy is Required"),
});

const AddRepeatingModal = ({
  showModal,
  closeModal,
  data,
  studyId,
  refreshPage,
}: any) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        ...values,
        category: "repeated_data",
        duration:
          typeof values?.duration === "number" ? values?.duration : null,
      };
      if (data?.id) {
        await http.patch(`/study/${studyId}/study-phase/${data?.id}`, body);
      } else {
        await http.post(`/study/${studyId}/study-phase`, body);
      }
      toastMessage(
        "success",
        data?.id
          ? "Repeating data updated successfully"
          : "Repeating data created successfully"
      );
      closeModal();
      setSubmitLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={data?.id ? "Edit Repeating Data" : "Add Repeating Data"}
          onCloseClick={closeModal}
        />
        <Formik
          initialValues={{
            name: data?.name || "",
            type: data?.type || "adverse_event",
            description: data?.description || "",
            duration: data?.duration ?? "",
            // namingStrategy: data?.namingStrategy || "",
            // manualEditing: data?.manualEditing || false,
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, values, setFieldValue }) => (
            <Form style={DrawerForm}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="phase-name">
                    Repeating Data Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Repeating Data Name"
                    id="phase-name"
                    {...getFieldProps("name")}
                    error={touched?.name && errors?.name ? true : false}
                    helperText={
                      touched?.name && errors?.name
                        ? (errors?.name as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="type">
                    Repeating Data Type <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    id="type"
                    value={values.type}
                    onChange={(e) => setFieldValue("type", e.target.value)}
                    error={touched?.type && errors?.type ? true : false}
                  >
                    {repeatedDataTypes.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={touched?.type && errors?.type ? true : false}
                  >
                    {touched?.type && errors?.type
                      ? (errors?.type as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="description">
                    Description
                  </FormLabel>
                  <TextField
                    multiline
                    placeholder="Description"
                    rows={4}
                    id="description"
                    {...getFieldProps("description")}
                    error={
                      touched?.description && errors?.description ? true : false
                    }
                    helperText={
                      touched?.description && errors?.description
                        ? (errors?.description as string)
                        : " "
                    }
                  />
                </FormControl>
              </Box>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="duration">
                  Offset from Enrollment (in days)
                </FormLabel>
                <TextField
                  type="number"
                  placeholder="Offset"
                  id="duration"
                  {...getFieldProps("duration")}
                  error={touched?.duration && errors?.duration ? true : false}
                  helperText={
                    touched?.duration && errors?.duration
                      ? (errors?.duration as string)
                      : " "
                  }
                />
              </FormControl>
              {/* <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="naming-strategy">
                    Repeating Data Name Naming Strategy{" "}
                    <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Repeating Data Naming Strategy"
                    id="naming-strategy"
                    {...getFieldProps("namingStrategy")}
                    error={
                      touched?.namingStrategy && errors?.namingStrategy
                        ? true
                        : false
                    }
                    helperText={
                      touched?.namingStrategy && errors?.namingStrategy
                        ? (errors?.namingStrategy as string)
                        : " "
                    }
                  />
                </FormControl>
              </Box>
              <Box sx={{ display: "flex" }}>
                <FormControl sx={InputWrapper}>
                  <FormControlLabel
                    sx={LabelStyle}
                    label="Allow manual editing of generated names"
                    control={
                      <Checkbox
                        checked={values?.manualEditing}
                        onChange={(event) => {
                          setFieldValue("manualEditing", event.target.checked);
                        }}
                      />
                    }
                  />
                </FormControl>
              </Box> */}
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        {data?.id ? "Save" : "Add Repeating Data"}
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddRepeatingModal;
